import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, ArrowLeft, Plus, Search, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, MessageSquare, ChartBar,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEM_TYPE_LABELS = { mc: "Multiple Choice", sata: "Select All", likert: "Likert" };
const ALLOWED_ITEM_TYPES = ["mc", "sata", "likert"];

export default function SurveyBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);

  // Inline edits
  const [titleDraft, setTitleDraft] = useState("");
  const [descDraft, setDescDraft] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  // Item picker
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerTypeFilter, setPickerTypeFilter] = useState("all");
  const [allItems, setAllItems] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  // Analytics
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const loadSurvey = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/surveys/${id}`, { withCredentials: true });
      setSurvey(data);
      setTitleDraft(data.title || "");
      setDescDraft(data.description || "");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load survey");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadSurvey(); }, [loadSurvey]);

  const openPicker = async () => {
    setShowPicker(true);
    setPickerLoading(true);
    try {
      const { data } = await axios.get(`${API}/items`, { withCredentials: true });
      setAllItems((data || []).filter((i) => ALLOWED_ITEM_TYPES.includes(i.item_type)));
    } catch {
      toast.error("Failed to load items");
    } finally {
      setPickerLoading(false);
    }
  };

  const attachedIds = useMemo(() => new Set((survey?.items || []).map((i) => i.id)), [survey]);

  const filteredItems = useMemo(() => {
    return allItems.filter((i) => {
      if (attachedIds.has(i.id)) return false;
      if (pickerTypeFilter !== "all" && i.item_type !== pickerTypeFilter) return false;
      if (pickerSearch.trim()) {
        const q = pickerSearch.toLowerCase();
        const stem = (i.stem || "").replace(/<[^>]*>/g, "").toLowerCase();
        const tagStr = Array.isArray(i.topic_tag)
          ? i.topic_tag.join(" ").toLowerCase()
          : String(i.topic_tag || "").toLowerCase();
        if (!(i.title || "").toLowerCase().includes(q) && !stem.includes(q) && !tagStr.includes(q)) return false;
      }
      return true;
    });
  }, [allItems, attachedIds, pickerTypeFilter, pickerSearch]);

  const attachItem = async (itemId) => {
    try {
      await axios.post(`${API}/surveys/${id}/items`, { item_id: itemId }, { withCredentials: true });
      toast.success("Added to survey");
      await loadSurvey();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add item");
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/surveys/${id}/items/${itemId}`, { withCredentials: true });
      toast.success("Removed");
      await loadSurvey();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  const reorder = async (newOrder) => {
    try {
      await axios.put(`${API}/surveys/${id}/items/reorder`, { item_ids: newOrder.map((i) => i.id) }, { withCredentials: true });
      setSurvey((s) => ({ ...s, items: newOrder }));
    } catch {
      toast.error("Reorder failed");
      loadSurvey();
    }
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...survey.items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    reorder(next);
  };

  const moveDown = (idx) => {
    if (idx === survey.items.length - 1) return;
    const next = [...survey.items];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    reorder(next);
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      await axios.put(`${API}/surveys/${id}`, { title: titleDraft, description: descDraft }, { withCredentials: true });
      toast.success("Saved");
      await loadSurvey();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingMeta(false);
    }
  };

  const togglePublish = async () => {
    try {
      const nextStatus = survey.status === "published" ? "draft" : "published";
      await axios.put(`${API}/surveys/${id}`, { status: nextStatus }, { withCredentials: true });
      toast.success(nextStatus === "published" ? "Published" : "Moved to draft");
      await loadSurvey();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status change failed");
    }
  };

  const toggleSetting = async (field, value) => {
    try {
      await axios.put(`${API}/surveys/${id}`, { [field]: value }, { withCredentials: true });
      setSurvey((s) => ({ ...s, [field]: value }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const openAnalytics = async () => {
    setShowAnalytics(true);
    setAnalyticsLoading(true);
    try {
      const { data } = await axios.get(`${API}/surveys/${id}/responses`, { withCredentials: true });
      setAnalytics(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load responses");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
    );
  }
  if (!survey) return null;

  const metaDirty = titleDraft !== survey.title || descDraft !== (survey.description || "");

  return (
    <div className="space-y-6" data-testid="survey-builder">
      <div>
        <button onClick={() => nav("/admin/surveys")} className="inline-flex items-center gap-1.5 text-xs font-body hover:underline" style={{ color: "var(--admin-container-muted)" }}>
          <ArrowLeft className="h-3 w-3" /> Back to Survey Library
        </button>
      </div>

      <div className="rounded-xl border p-4 sm:p-5 space-y-4" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-start justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <Input
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              className="text-lg sm:text-xl font-heading font-bold border-0 px-0 bg-transparent focus-visible:ring-0"
              style={{ color: "var(--admin-container-text)" }}
              data-testid="survey-builder-title"
            />
            <Textarea
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              placeholder="Description..."
              rows={2}
              className="border-0 px-0 resize-none text-sm bg-transparent mt-2 focus-visible:ring-0"
              style={{ color: "var(--admin-container-muted)" }}
              data-testid="survey-builder-desc"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <Badge
              className="text-[10px] font-body"
              style={survey.status === "published"
                ? { backgroundColor: "color-mix(in srgb, #0d9488 20%, transparent)", color: "#0d9488", border: "1px solid #0d9488" }
                : { backgroundColor: "var(--admin-bg)", color: "var(--admin-container-muted)", border: "1px solid var(--admin-container-border)" }
              }
            >
              {survey.status || "draft"}
            </Badge>
            {metaDirty && (
              <Button onClick={saveMeta} disabled={savingMeta} size="sm" className="text-xs font-body gap-1.5"
                style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
                data-testid="survey-save-meta-btn">
                {savingMeta && <Loader2 className="h-3 w-3 animate-spin" />} Save
              </Button>
            )}
            <Button onClick={togglePublish} size="sm" variant="outline" className="text-xs font-body gap-1.5"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
              data-testid="survey-toggle-publish-btn">
              {survey.status === "published" ? <><EyeOff className="h-3 w-3" /> Unpublish</> : <><Eye className="h-3 w-3" /> Publish</>}
            </Button>
            <Button onClick={openAnalytics} size="sm" variant="outline" className="text-xs font-body gap-1.5"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
              data-testid="survey-analytics-btn">
              <ChartBar className="h-3 w-3" /> Responses
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t" style={{ borderColor: "var(--admin-container-border)" }}>
          <div className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: "var(--admin-bg)" }}>
            <div>
              <Label className="text-xs font-body font-medium" style={{ color: "var(--admin-container-text)" }}>Anonymous</Label>
              <p className="text-[11px] font-body mt-0.5" style={{ color: "var(--admin-container-muted)" }}>Responses not linked to learner.</p>
            </div>
            <Switch checked={!!survey.anonymous} onCheckedChange={(v) => toggleSetting("anonymous", v)} data-testid="survey-builder-anonymous-switch" />
          </div>
          <div className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: "var(--admin-bg)" }}>
            <div>
              <Label className="text-xs font-body font-medium" style={{ color: "var(--admin-container-text)" }}>Allow resubmission</Label>
              <p className="text-[11px] font-body mt-0.5" style={{ color: "var(--admin-container-muted)" }}>Let learners resubmit.</p>
            </div>
            <Switch checked={!!survey.allow_resubmit} onCheckedChange={(v) => toggleSetting("allow_resubmit", v)} data-testid="survey-builder-resubmit-switch" />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-body font-bold uppercase tracking-wider" style={{ color: "var(--admin-container-muted)" }}>
            Items ({survey.items?.length || 0})
          </h2>
          <Button onClick={openPicker} size="sm" className="gap-1.5 text-xs font-body"
            style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
            data-testid="survey-add-item-btn">
            <Plus className="h-3 w-3" /> Add Item
          </Button>
        </div>

        {(survey.items?.length || 0) === 0 ? (
          <div className="text-center py-12 rounded-xl border" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
            <MessageSquare className="h-8 w-8 mx-auto mb-2" style={{ color: "var(--admin-container-muted)" }} />
            <p className="text-sm font-body font-bold" style={{ color: "var(--admin-container-text)" }}>No items yet</p>
            <p className="text-xs font-body mt-1" style={{ color: "var(--admin-container-muted)" }}>
              Add MC, Select-All, or Likert items from your Question Bank.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
            <div className="divide-y" style={{ borderColor: "var(--admin-container-border)" }}>
              {survey.items.map((it, idx) => (
                <div key={it.id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-white/[0.02]" data-testid={`survey-item-${it.id}`}>
                  <div className="col-span-1 font-body text-sm font-medium" style={{ color: "var(--admin-container-muted)" }}>
                    {idx + 1}.
                  </div>
                  <div className="col-span-7 min-w-0">
                    <p className="text-sm font-body font-medium truncate" style={{ color: "var(--admin-container-text)" }}>{it.title}</p>
                  </div>
                  <div className="col-span-2">
                    <Badge variant="outline" className="text-[10px] font-body"
                      style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
                      {ITEM_TYPE_LABELS[it.item_type] || it.item_type}
                    </Badge>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <button onClick={() => moveUp(idx)} disabled={idx === 0}
                      className="p-1.5 rounded disabled:opacity-20 hover:bg-white/[0.04]"
                      data-testid={`survey-item-up-${it.id}`}>
                      <ChevronUp className="h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
                    </button>
                    <button onClick={() => moveDown(idx)} disabled={idx === survey.items.length - 1}
                      className="p-1.5 rounded disabled:opacity-20 hover:bg-white/[0.04]"
                      data-testid={`survey-item-down-${it.id}`}>
                      <ChevronDown className="h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
                    </button>
                    <button onClick={() => removeItem(it.id)} className="p-1.5 rounded hover:bg-red-500/10"
                      data-testid={`survey-item-remove-${it.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400/70" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Item picker */}
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
          style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}
        >
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>Add Survey Item</DialogTitle>
            <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>
              Only MC, Select-All, and Likert items are available for surveys.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
              <Input
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search items..."
                className="pl-8 h-8 text-xs"
                style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                data-testid="survey-picker-search"
              />
            </div>
            <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: "var(--admin-bg)", border: "1px solid var(--admin-container-border)" }}>
              {[["all", "All"], ["mc", "MC"], ["sata", "SATA"], ["likert", "Likert"]].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => setPickerTypeFilter(v)}
                  className="px-2 py-1 text-[11px] font-body rounded-md"
                  style={{
                    backgroundColor: pickerTypeFilter === v ? "color-mix(in srgb, var(--primary-color) 25%, transparent)" : "transparent",
                    color: "var(--admin-container-text)",
                    opacity: pickerTypeFilter === v ? 1 : 0.55,
                  }}
                  data-testid={`survey-picker-filter-${v}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto mt-2 rounded-lg border" style={{ borderColor: "var(--admin-container-border)" }}>
            {pickerLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
            ) : filteredItems.length === 0 ? (
              <p className="text-xs font-body text-center py-10" style={{ color: "var(--admin-container-muted)" }}>
                No items match. Create MC, SATA, or Likert items in the Question Bank.
              </p>
            ) : (
              <div className="divide-y" style={{ borderColor: "var(--admin-container-border)" }}>
                {filteredItems.map((i) => (
                  <button
                    key={i.id}
                    onClick={() => attachItem(i.id)}
                    className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-white/[0.04] transition-colors"
                    data-testid={`survey-picker-item-${i.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-body font-medium truncate" style={{ color: "var(--admin-container-text)" }}>{i.title}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] font-body shrink-0"
                      style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
                      {ITEM_TYPE_LABELS[i.item_type] || i.item_type}
                    </Badge>
                    <Plus className="h-3 w-3 flex-shrink-0" style={{ color: "var(--primary-color)" }} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3">
            <Button variant="outline" size="sm" onClick={() => setShowPicker(false)} className="text-xs font-body"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analytics */}
      <Dialog open={showAnalytics} onOpenChange={setShowAnalytics}>
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
          style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}
        >
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>Responses</DialogTitle>
            <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>
              {analytics?.anonymous ? "Anonymous — responses not linked to learners." : "Per-item summaries."}
              {analytics ? ` · Total: ${analytics.total_responses}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 mt-2">
            {analyticsLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
            ) : !analytics || analytics.total_responses === 0 ? (
              <p className="text-xs font-body text-center py-10" style={{ color: "var(--admin-container-muted)" }}>
                No responses yet.
              </p>
            ) : (
              (survey.items || []).map((it) => {
                const bucket = analytics.per_item?.[it.id];
                if (!bucket) return (
                  <div key={it.id} className="rounded-lg border p-3" style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)" }}>
                    <p className="text-xs font-body font-medium" style={{ color: "var(--admin-container-text)" }}>{it.title}</p>
                    <p className="text-[11px] font-body mt-1" style={{ color: "var(--admin-container-muted)" }}>No responses for this item.</p>
                  </div>
                );
                return (
                  <div key={it.id} className="rounded-lg border p-3 space-y-2" style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)" }}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-body font-medium" style={{ color: "var(--admin-container-text)" }}>{it.title}</p>
                      <span className="text-[10px] font-body" style={{ color: "var(--admin-container-muted)" }}>
                        {bucket.count} responses {bucket.likert_avg != null && `· avg ${bucket.likert_avg}`}
                      </span>
                    </div>
                    {it.item_type === "likert" && bucket.likert_count > 0 && (
                      <p className="text-xs font-body" style={{ color: "var(--admin-container-text)" }}>
                        Average: {bucket.likert_avg}
                      </p>
                    )}
                    {(it.item_type === "mc" || it.item_type === "sata") && it.answer_options && (
                      <div className="space-y-1.5">
                        {it.answer_options.map((opt) => {
                          const n = bucket.selected_option_counts?.[opt.option_id] || 0;
                          const pct = bucket.count > 0 ? Math.round((n / bucket.count) * 100) : 0;
                          return (
                            <div key={opt.option_id} className="flex items-center gap-2">
                              <span className="text-[11px] font-body flex-1 truncate" style={{ color: "var(--admin-container-text)" }}>{opt.text}</span>
                              <div className="flex items-center gap-1.5 w-32">
                                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "var(--admin-container-border)" }}>
                                  <div className="h-full" style={{ width: `${pct}%`, backgroundColor: "var(--primary-color)" }} />
                                </div>
                                <span className="text-[10px] font-body w-10 text-right" style={{ color: "var(--admin-container-muted)" }}>{n} ({pct}%)</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
